import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ID } from "appwrite";
import { account, createUserProfileDocument } from "../lib/appwrite.js";
import AuthLoadingScreen from "../components/AuthLoadingScreen.jsx";
import { signInWithEmailPassword } from "../lib/authSession.js";
import { useAuth } from "../context/AuthContext.jsx";
import { redirectAfterAuth, resolvePostAuthDestination } from "../lib/authRedirect.js";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { status: authStatus, postLoginPath, refetch, user, role } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accountKind, setAccountKind] = useState("coach");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (formStatus === "loading") return;
    if (authStatus !== "authenticated" || !postLoginPath || !user) return;
    const dest = resolvePostAuthDestination({
      role,
      postLoginPath,
      fromPathname: null,
    });
    navigate(dest, { replace: true });
  }, [authStatus, postLoginPath, navigate, formStatus, user, role]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match.");
      setFormStatus("error");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      setFormStatus("error");
      return;
    }
    setFormStatus("loading");
    setError("");
    try {
      const created = await account.create(ID.unique(), email, password, name);
      await signInWithEmailPassword(email, password);
      await account.updatePrefs({ accountKind });
      await createUserProfileDocument(created.$id, name, email, accountKind);
      await redirectAfterAuth({ refetch, navigate, fromPathname: null });
    } catch (err) {
      if (err?.type === "user_already_exists") {
        setError(
          "An account with this email already exists. Try signing in instead."
        );
      } else {
        setError(err?.message ?? "Something went wrong. Please try again.");
      }
      setFormStatus("error");
    }
  }

  const backgrounds = (
    <>
      <div
        className="pointer-events-none absolute left-1/2 top-[40%] size-[320px] -translate-x-[120%] rounded-full bg-hc-warm/15 blur-[64px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] size-[288px] translate-x-[20%] rounded-full bg-hc-secondary/20 blur-[64px]"
        aria-hidden
      />
    </>
  );

  if (authStatus === "loading") {
    return <AuthLoadingScreen />;
  }

  return (
    <div className="hc-page-shell relative min-h-screen w-full overflow-hidden font-sans">
      {backgrounds}

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[384px] flex-col items-center justify-center px-4 py-16">
        <img
          src="/logo_full.png"
          alt="Hey Coach AI logo"
          className="w-full max-w-[384px] h-auto"
        />

        <div className="w-full max-w-[384px] rounded-[16px] border border-hc-surface-border-strong bg-hc-surface p-7 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.35)]">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mb-5 flex items-center gap-1.5 text-xs text-hc-text-dim hover:text-hc-text"
          >
            <i className="icon-arrow-left leading-none" aria-hidden="true" />
            Back to sign in
          </button>

          <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
            Create an account
          </h1>
          <p className="mt-2 text-xs leading-4 text-hc-text-dim">
            Create your account — choose Coach or Client to get started.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            { }
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-xs font-medium text-hc-text-muted">
                Full name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] px-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
              />
            </div>

            { }
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-xs font-medium text-hc-text-muted">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] px-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
              />
            </div>

            { }
            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-xs font-medium text-hc-text-muted">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] py-3 pr-12 pl-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-hc-text-dim"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`${showPassword ? "icon-eye-off" : "icon-eye"} block leading-none`} aria-hidden="true" />
                </button>
              </div>
            </div>

            { }
            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-xs font-medium text-hc-text-muted">
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] py-3 pr-12 pl-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-hc-text-dim"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  <i className={`${showConfirm ? "icon-eye-off" : "icon-eye"} block leading-none`} aria-hidden="true" />
                </button>
              </div>
            </div>

            { }
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-hc-text-muted">I am a…</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAccountKind("coach")}
                  className={`flex h-[46px] items-center justify-center rounded-[14px] border text-sm font-medium tracking-[-0.15px] transition ${accountKind === "coach"
                    ? "border-hc-primary bg-hc-primary/10 text-hc-primary"
                    : "border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] text-hc-text-muted hover:border-hc-primary/40"
                    }`}
                >
                  Coach
                </button>
                <button
                  type="button"
                  onClick={() => setAccountKind("client")}
                  className={`flex h-[46px] items-center justify-center rounded-[14px] border text-sm font-medium tracking-[-0.15px] transition ${accountKind === "client"
                    ? "border-hc-primary bg-hc-primary/10 text-hc-primary"
                    : "border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] text-hc-text-muted hover:border-hc-primary/40"
                    }`}
                >
                  Client
                </button>
              </div>
            </div>

            {formStatus === "error" && (
              <p className="rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={formStatus === "loading"}
              className="relative mt-2 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 disabled:opacity-60"
            >
              {formStatus === "loading" ? "Creating account…" : "Create account"}
              {formStatus !== "loading" && <i className="icon-arrow-right" aria-hidden="true" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
